<template>
  <div class="statistics-page page-container">
    <!-- 顶部统计卡片 -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon notes">
              <i class="el-icon-tickets"></i>
            </div>
            <div class="stat-info">
              <div class="stat-title">总记事数</div>
              <div class="stat-number">{{ statistics.totalNotes }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon items">
              <i class="el-icon-goods"></i>
            </div>
            <div class="stat-info">
              <div class="stat-title">总物品数</div>
              <div class="stat-number">{{ statistics.totalItems }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon categories">
              <i class="el-icon-menu"></i>
            </div>
            <div class="stat-info">
              <div class="stat-title">分类数量</div>
              <div class="stat-number">{{ statistics.totalCategories }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon locations">
              <i class="el-icon-location-outline"></i>
            </div>
            <div class="stat-info">
              <div class="stat-title">位置数量</div>
              <div class="stat-number">{{ statistics.totalLocations }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <!-- 超级管理员可见的统计 -->
    <el-row v-if="isAdmin" :gutter="20" class="admin-stats-cards">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon users">
              <i class="el-icon-user"></i>
            </div>
            <div class="stat-info">
              <div class="stat-title">用户总数</div>
              <div class="stat-number">{{ adminStatistics.userCount }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="20" class="charts-row">
      <el-col :span="16">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>每月记事统计</span>
          </div>
          <div class="chart-container">
            <canvas ref="lineChart" height="300"></canvas>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>各类物品数量</span>
          </div>
          <div class="chart-container">
            <canvas ref="pieChart" height="300"></canvas>
          </div>
        </el-card>
      </el-col>
    </el-row>
    
    <!-- 超级管理员可见的用户统计表格 -->
    <el-row v-if="isAdmin" :gutter="20" class="admin-stats-row">
      <el-col :span="24">
        <el-card class="admin-chart-card">
          <div slot="header" class="chart-header">
            <span>各用户记事与物品统计</span>
          </div>
          <el-table :data="adminStatistics.userStats" style="width: 100%" max-height="400" show-summary>
            <el-table-column prop="nickName" label="用户昵称" width="180"></el-table-column>
            <el-table-column prop="totalNotes" label="记事数量" width="180"></el-table-column>
            <el-table-column prop="totalItems" label="物品数量"></el-table-column>
          </el-table>
          <div class="table-footer">
            共 {{ adminStatistics.userStats.length }} 条数据
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getTotalCount, getMonthNoteCount, getItemTypeCount, getUserTotalCount, getAllUserCount } from '@/api/statistics'
import Chart from 'chart.js/auto'
import { mapState } from 'vuex'

export default {
  name: 'DataStatistics',
  data() {
    return {
      statistics: {
        totalNotes: 0,
        totalItems: 0,
        totalCategories: 0,
        totalLocations: 0
      },
      adminStatistics: {
        userCount: 0,
        userStats: []
      },
      lineChart: null,
      pieChart: null
    }
  },
  computed: {
    ...mapState('user', ['userInfo']),
    isAdmin() {
      // 检查用户是否为超级管理员
      return this.userInfo && this.userInfo.isAdmin === true
    }
  },
  mounted() {
    this.fetchTotalCount()
    this.fetchChartData()
    // 如果是超级管理员，获取额外的统计信息
    if (this.isAdmin) {
      this.fetchAdminStats()
    }
  },
  beforeDestroy() {
    this.destroyCharts()
  },
  // 添加 activated 和 deactivated 钩子，支持 keep-alive
  activated() {
    this.fetchTotalCount()
    this.fetchChartData()
    if (this.isAdmin) {
      this.fetchAdminStats()
    }
  },
  deactivated() {
    this.destroyCharts()
  },
  methods: {
    async fetchTotalCount() {
      try {
        const response = await getTotalCount()
        if (response.data.code === 200) {
          this.statistics = response.data.data
        } else {
          this.$message.error(response.data.msg || '获取统计数据失败')
        }
      } catch (error) {
        this.$message.error('获取统计数据失败')
        console.error(error)
      }
    },
    async fetchChartData() {
      try {
        const [monthResponse, typeResponse] = await Promise.all([
          getMonthNoteCount(),
          getItemTypeCount()
        ])

        if (monthResponse.data.code === 200 && typeResponse.data.code === 200) {
          this.renderLineChart(monthResponse.data.data)
          this.renderPieChart(typeResponse.data.data)
        } else {
          this.$message.error('获取图表数据失败')
        }
      } catch (error) {
        this.$message.error('获取图表数据失败')
        console.error(error)
      }
    },
    async fetchAdminStats() {
      try {
        const [userCountResponse, userStatsResponse] = await Promise.all([
          getUserTotalCount(),
          getAllUserCount()
        ])

        if (userCountResponse.data.code === 200) {
          this.adminStatistics.userCount = userCountResponse.data.data
        }

        if (userStatsResponse.data.code === 200) {
          this.adminStatistics.userStats = userStatsResponse.data.data
        }
      } catch (error) {
        this.$message.error('获取管理员统计数据失败')
        console.error(error)
      }
    },
    renderLineChart(data) {
      const ctx = this.$refs.lineChart.getContext('2d')
      
      // 销毁已有图表实例
      if (this.lineChart) {
        this.lineChart.destroy()
      }
      
      this.lineChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [{
            label: '记事数量',
            data: data.data,
            borderColor: '#409EFF',
            backgroundColor: 'rgba(64, 158, 255, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                autoSkip: false,
                maxRotation: 45,
                minRotation: 45
              }
            },
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      })
    },
    renderPieChart(data) {
      const ctx = this.$refs.pieChart.getContext('2d')
      
      // 销毁已有图表实例
      if (this.pieChart) {
        this.pieChart.destroy()
      }
      
      this.pieChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: data.labels,
          datasets: [{
            data: data.data,
            backgroundColor: [
              '#409EFF',
              '#67C23A',
              '#E6A23C',
              '#F56C6C',
              '#909399',
              '#FFB6C1',
              '#8A2BE2',
              '#00FFFF'
            ]
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right'
            }
          }
        }
      })
    },
    // 统一销毁图表的方法
    destroyCharts() {
      if (this.lineChart) {
        this.lineChart.destroy()
        this.lineChart = null
      }
      if (this.pieChart) {
        this.pieChart.destroy()
        this.pieChart = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.statistics-page {
  padding: 20px;
  
  &.page-container {
    margin-top: -10px;
  }
  
  .stats-cards {
    margin-bottom: 20px;
  }
  
  .admin-stats-cards {
    margin-bottom: 20px;
  }
  
  .stat-card {
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    
    .stat-content {
      display: flex;
      align-items: center;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        margin-right: 20px;
        
        &.notes {
          background-color: rgba(64, 158, 255, 0.1);
          color: #409EFF;
        }
        
        &.items {
          background-color: rgba(103, 194, 58, 0.1);
          color: #67C23A;
        }
        
        &.categories {
          background-color: rgba(230, 162, 60, 0.1);
          color: #E6A23C;
        }
        
        &.locations {
          background-color: rgba(245, 108, 108, 0.1);
          color: #F56C6C;
        }
        
        &.users {
          background-color: rgba(144, 147, 153, 0.1);
          color: #909399;
        }
      }
      
      .stat-info {
        .stat-title {
          font-size: 14px;
          color: #909399;
          margin-bottom: 5px;
        }
        
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #303133;
        }
      }
    }
  }
  
  .charts-row {
    margin-bottom: 20px;
    
    .chart-card {
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      
      .chart-header {
        font-weight: bold;
        color: #303133;
      }
      
      .chart-container {
        position: relative;
        height: 300px;
      }
    }
  }
  
  .admin-stats-row {
    .admin-chart-card {
      border-radius: 8px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      
      .chart-header {
        font-weight: bold;
        color: #303133;
      }
      
      .table-footer {
        margin-top: 15px;
        text-align: right;
        color: #606266;
        font-size: 14px;
      }
    }
  }
}
</style>